import {Inject, Injectable, InjectionToken} from '@angular/core';
import {Preferences} from '@capacitor/preferences';

const NAVIGATION_GROUP_PREFERENCES_KEY_PREFIX = 'item-tracker.navigation-groups.v1.';

type NavigationGroupPreferences = {
  version: 1;
  groups: Record<string, boolean>;
};

type NavigationPreferencesStorage = Pick<typeof Preferences, 'get' | 'set'>;

export const NAVIGATION_PREFERENCES_STORAGE = new InjectionToken<NavigationPreferencesStorage>(
  'NAVIGATION_PREFERENCES_STORAGE',
  {providedIn: 'root', factory: () => Preferences},
);

@Injectable({providedIn: 'root'})
export class NavigationGroupPreferencesService {
  constructor(@Inject(NAVIGATION_PREFERENCES_STORAGE) private readonly preferences: NavigationPreferencesStorage) {}

  async read(accountId: string): Promise<Record<string, boolean> | null> {
    if (!accountId) {
      return null;
    }

    try {
      const storedPreference = await this.preferences.get({key: this.buildKey(accountId)});
      if (!storedPreference.value) {
        return null;
      }

      const parsed: unknown = JSON.parse(storedPreference.value);
      return this.isNavigationGroupPreferences(parsed) ? {...parsed.groups} : null;
    } catch {
      return null;
    }
  }

  async write(accountId: string, groups: Record<string, boolean>): Promise<void> {
    if (!accountId) {
      return;
    }

    const preference: NavigationGroupPreferences = {version: 1, groups: {...groups}};
    try {
      await this.preferences.set({
        key: this.buildKey(accountId),
        value: JSON.stringify(preference),
      });
    } catch {
      // Keep the in-memory navigation state usable when persistence is unavailable.
    }
  }

  private buildKey(accountId: string): string {
    return `${NAVIGATION_GROUP_PREFERENCES_KEY_PREFIX}${encodeURIComponent(accountId)}`;
  }

  private isNavigationGroupPreferences(value: unknown): value is NavigationGroupPreferences {
    if (!value || typeof value !== 'object') {
      return false;
    }

    const candidate = value as Partial<NavigationGroupPreferences>;
    if (candidate.version !== 1 || !candidate.groups || typeof candidate.groups !== 'object' || Array.isArray(candidate.groups)) {
      return false;
    }

    return Object.entries(candidate.groups).every(([groupId, expanded]) => (
      groupId.length > 0 && typeof expanded === 'boolean'
    ));
  }
}
